import emitter from "@/utils/eventBus";
import axios from "axios";
import Loader from "@/components/Loader.vue";
import moment from "moment";

import { fetchGraphData } from "../DashboardUtils/GraphData";
import { getDeviceStatus } from "@/utils/eventBus";

import { StatsCard, AnimatedNumber } from "@/components";
import { classroomByPath } from "../../../utils/userInfo";
import SimpleLineChart from "../SimpleLineChart.vue";
import EnergyInsights from "./EnergyInsights.vue";
import SubjectConsumptionTable from "./SubjectConsumptionTable.vue";
import SubjectPieChart from "./SubjectPieChart.vue";

export default {
  components: {
    StatsCard,
    AnimatedNumber,
    SimpleLineChart,
    Loader,
    EnergyInsights,
    SubjectConsumptionTable,
    SubjectPieChart,
  },

  async mounted() {
    document.addEventListener("click", this.handleClickOutside);

    this.classroom = classroomByPath[this.$route.path];
    if (!this.classroom) return;

    const deviceKey = this.classroom.token.split("|")[0];
    const initialStatus = getDeviceStatus(deviceKey);
    if (typeof initialStatus === "boolean") {
      this.statusText = initialStatus ? "Online" : "Offline";
    }

    emitter.off("device-status");
    emitter.on("device-status", ({ device, isOnline }) => {
      if (device === this.classroom.token.split("|")[0]) {
        this.statusText = isOnline ? "Online" : "Offline";
      }
    });

    setInterval(() => {
      this.checkTimeAndRefresh();
    }, 60000);

    this.onMounted();
    this.fetchCurrentSubject();
    this.refreshCharts();
    this.updateSmartTips();

    emitter.off("update-values");
    emitter.on("update-values", ({ wsMessage, device }) => {
      if (!this.classroom) return;
      if (
        this.$route.path === this.classroom.path &&
        device === this.classroom.token.split("|")[0]
      ) {
        const phases = ["L1", "L2", "L3"];

        const avgPhases = (obj) => {
          let sum = 0, count = 0;
          phases.forEach((p) => {
            if (typeof obj?.[p] === "number") { sum += obj[p]; count++; }
          });
          return count > 0 ? sum / count : 0;
        };

        const sumPhases = (obj) => {
          let sum = 0;
          phases.forEach((p) => {
            if (typeof obj?.[p] === "number") sum += obj[p];
          });
          return sum > 0 ? sum : 0;
        };

        this.power = Number(sumPhases(wsMessage.data.P));
        this.energy = Number(sumPhases(wsMessage.data.E_I));
        this.energy = this.energy < 1000 ? this.energy : this.energy / 1000;
      }
    });
  },

  beforeDestroy() {
    emitter.off("update-values");
    emitter.off("device-status");
    document.removeEventListener("click", this.handleClickOutside);
  },

  watch: {
    "$route.path": {
      handler: async function () {
        this.classroom = classroomByPath[this.$route.path];
        if (!this.classroom) return;

        this.graphTimeRange = "Deň";

        this.fetchCurrentSubject();
        this.refreshCharts();
        this.onMounted();
        this.smartTipsText = "Odporúčania sa aktuálne načítavajú...";
        this.updateSmartTips();

        const deviceKey = this.classroom.token.split("|")[0];
        const deviceStatus = getDeviceStatus(deviceKey);
        this.statusText = deviceStatus ? "Online" : "Offline";
      },
    },
  },

  computed: {
    isLoadingAny() {
      return this.loadingGraphData || this.subjectDataLoading || this.loadingTips;
    },
    // Kalendárové dni pre výber rozsahu
    daysInMonth() {
      const days = [];
      const year = this.currentMonth.getFullYear();
      const month = this.currentMonth.getMonth();
      const first = new Date(year, month, 1);
      const last = new Date(year, month + 1, 0);
      const startOffset = (first.getDay() + 6) % 7;

      for (let i = 0; i < startOffset; i++) days.push({ date: null, empty: true });
      for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
        days.push({ date: new Date(d), empty: false });
      }
      return days;
    },

    gridConsumed() {
      return Number(this.totalConsumed) || 0;
    },
    gridConsumedFinancial() {
      return +(this.gridConsumed * this.energyPricePerKwh).toFixed(2);
    },
    totalConsumedFinancial() {
      return +(Number(this.totalConsumed) * this.energyPricePerKwh).toFixed(2);
    },
  },

  methods: {
    async fetchSubjectData() {
      const agent = classroomByPath[this.$route.path];
      if (!agent) return;
      this.subjectDataLoading = true;
      try {
        const { from, now } = this._getSubjectTimeRange();
        const res = await axios.post(`${agent.agentUrl}/rest/subjectConsumption`, {
          startTimestamp: from,
          endTimestamp: now,
          subject: "",
        });
        this.subjectDataList = res.data?.results || [];
      } catch (e) {}
      finally { this.subjectDataLoading = false; }
    },

    // Pomocná metóda pre rozsah spotreby predmetov
    _getSubjectTimeRange() {
      const oneDay = 24 * 3600;
      const yearStart = Math.floor(new Date(new Date().getFullYear(), 0, 1).getTime() / 1000);
      let now = Math.floor(Date.now() / 1000);
      let from;
      switch (this.graphTimeRange) {
        case "Deň":   from = Math.floor(new Date().setHours(0,0,0,0) / 1000); break;
        case "Týždeň": from = now - 7 * oneDay; break;
        case "Mesiac": from = now - 30 * oneDay; break;
        case "Rok":    from = Math.floor(new Date(new Date().getFullYear(), 0, 1).getTime() / 1000); break;
        case "Custom":
          from = this.customRangeStart ? Math.floor(new Date(this.customRangeStart).setHours(0,0,0,0) / 1000) : now - oneDay;
          now  = this.customRangeEnd   ? Math.floor(new Date(this.customRangeEnd).setHours(23,59,59,0) / 1000) : now;
          break;
        default: from = now - oneDay;
      }
      // Validácia: startTimestamp nesmie byť menší ako začiatok roka
      if (from < yearStart) {
        from = yearStart;
      }
      return { from, now };
    },

    // === AKTUÁLNY PREDMET ===
    async fetchCurrentSubject() {
      const agent = classroomByPath[this.$route.path];
      if (!agent) return;
      try {
        const res = await axios.post(`${agent.agentUrl}/rest/currentSubject`, {});
        this.currentSubject = res.data?.subject_name || "Neznámy predmet";
      } catch {
        this.currentSubject = "Nedostupné";
      }
    },

    // === SMART TIPS ===
    async updateSmartTips() {
      this.loadingTips = true;
      this.setLoaderTimeout('tips');
      const oneDay = 24 * 3600;
      const yearStart = Math.floor(new Date(new Date().getFullYear(), 0, 1).getTime() / 1000);
      let now = Math.floor(Date.now() / 1000);
      let from, granularity;

      switch (this.graphTimeRange) {
        case "Deň":
          from = Math.floor(moment().startOf("day").valueOf() / 1000);
          granularity = "block";
          break;
        case "Týždeň":
          from = moment().subtract(7, "days").unix();
          granularity = "day";
          break;
        case "Mesiac":
          from = moment().subtract(30, "days").unix();
          granularity = "day";
          break;
        case "Rok":
          from = moment().startOf("year").unix();
          granularity = "month";
          break;
        case "Custom": {
          from = Math.floor(new Date(this.customRangeStart).setHours(0, 0, 0, 0) / 1000);
          const end = Math.floor(new Date(this.customRangeEnd).setHours(0, 0, 0, 0) / 1000);
          const duration = end - from;
          if (duration <= 0) {
            this.loadingTips = false;
            this.clearLoaderTimeout('tips');
            return;
          }
          granularity = duration <= oneDay ? "block" : duration <= 30 * oneDay ? "day" : "month";
          now = end;
          break;
        }
        default:
          from = moment().startOf("year").unix();
          granularity = "month";
      }

      // Validácia: startTimestamp nesmie byť menší ako začiatok roka
      if (from < yearStart) {
        from = yearStart;
      }

      const agent = classroomByPath[this.$route.path];
      if (!agent) {
        this.loadingTips = false;
        this.clearLoaderTimeout('tips');
        return;
      }

      try {
        const tipsRes = await axios.post(`${agent.agentUrl}/rest/smartTips`, {
          startTimestamp: from,
          endTimestamp: now,
          granularity,
        });
        this.smartTipsText = tipsRes.data.text;
      } catch (err) {}
      finally {
        this.loadingTips = false;
        this.clearLoaderTimeout('tips');
      }
    },

    // === GRAFY ===
    async refreshCharts() {
      this.loadingGraphData = true;
      this.setLoaderTimeout('graphs');

      try {
        const chartData = await fetchGraphData(
          this.classroom,
          this.chartData,
          this.graphTimeRange,
          this.customRangeStart,
          this.customRangeEnd,
          this.$route.path
        );

        this.chartData = chartData;

        this.totalConsumed = +(
          (this.chartData?.datasets || [])
            .filter((ds) => !ds.label.includes("Predikcia"))
            .flatMap((ds) => ds.data)
            .filter((v) => !isNaN(v))
            .reduce((sum, v) => sum + v, 0)
        ).toFixed(3);

        const daysMap = { Deň: 1, Týždeň: 7, Mesiac: 30, Rok: 365 };
        this.currentPeriodDays = daysMap[this.graphTimeRange] || 1;
        this.currentPeriodLabel = {
          Deň: "Tento deň",
          Týždeň: "Posledný týždeň",
          Mesiac: "Posledných 30 dní",
          Rok: "Tento rok",
        }[this.graphTimeRange] || "zvolené obdobie";

        await this.fetchSubjectData();
      } finally {
        this.loadingGraphData = false;
        this.clearLoaderTimeout('graphs');
      }
    },

    // === ČASOVÝ ROZSAH ===
    setGraphTimeRange(range) {
      this.graphTimeRange = range;
      const yearStart = new Date(new Date().getFullYear(), 0, 1);
      switch (range) {
        case "Deň":
          this.customRangeStart = moment().startOf("day").toDate();
          this.customRangeEnd = moment().endOf("day").toDate();
          break;
        case "Týždeň":
          this.customRangeStart = moment().subtract(7, "days").startOf("day").toDate();
          this.customRangeEnd = moment().endOf("day").toDate();
          break;
        case "Mesiac":
          this.customRangeStart = moment().subtract(30, "days").startOf("day").toDate();
          this.customRangeEnd = moment().endOf("day").toDate();
          break;
        case "Rok":
        default:
          this.customRangeStart = moment().startOf("year").toDate();
          this.customRangeEnd = moment().endOf("day").toDate();
          break;
      }
      // Validácia: startTimestamp nesmie byť menší ako začiatok roka
      if (this.customRangeStart < yearStart) {
        this.customRangeStart = yearStart;
      }
      this.refreshCharts();
      this.updateSmartTips();
    },

    activateCustomRange() {
      this.graphTimeRange = "Custom";
      this.selectedStart = null;
      this.selectedEnd = null;
      this.showCustomCalendar = true;

      this.$nextTick(() => {
        const buttonEl = this.$refs.customButton?.$el || this.$refs.customButton;
        if (buttonEl) {
          const btnRect = buttonEl.getBoundingClientRect();
          this.calendarPosition = {
            top: `${btnRect.bottom + 8}px`,
            left: `${btnRect.left}px`,
          };
        }
      });
    },

    selectDay(date) {
      const normalize = (d) => { const n = new Date(d); n.setHours(0,0,0,0); return n; };
      const normalizedDate = normalize(date);
      const normalizedStart = this.selectedStart ? normalize(this.selectedStart) : null;

      if (!this.selectedStart || (this.selectedStart && this.selectedEnd)) {
        this.selectedStart = date;
        this.selectedEnd = null;
      } else if (normalizedDate >= normalizedStart) {
        this.selectedEnd = date;
        this.finalizeRange();
      } else {
        this.selectedStart = date;
        this.selectedEnd = null;
      }
    },

    finalizeRange() {
      this.customRangeStart = new Date(this.selectedStart.setHours(0, 0, 0, 0));
      this.customRangeEnd = new Date(this.selectedEnd.setHours(23, 59, 59, 999));
      
      // Validácia: startTimestamp nesmie byť menší ako začiatok roka
      const yearStart = new Date(new Date().getFullYear(), 0, 1);
      if (this.customRangeStart < yearStart) {
        this.customRangeStart = yearStart;
      }
      
      this.showCustomCalendar = false;
      this.refreshCharts();
      this.updateSmartTips();
    },

    isSelected(date) {
      return (
        (this.selectedStart && date.toDateString() === this.selectedStart.toDateString()) ||
        (this.selectedEnd && date.toDateString() === this.selectedEnd.toDateString())
      );
    },
    isInRange(date) {
      return (
        this.selectedStart && this.selectedEnd &&
        date > this.selectedStart && date < this.selectedEnd
      );
    },
    isToday(date) {
      return date.toDateString() === new Date().toDateString();
    },
    prevMonth() {
      const prev = new Date(this.currentMonth);
      prev.setMonth(prev.getMonth() - 1);
      this.currentMonth = prev;
    },
    nextMonth() {
      const next = new Date(this.currentMonth);
      next.setMonth(next.getMonth() + 1);
      this.currentMonth = next;
    },
    handleClickOutside(e) {
      if (
        this.showCustomCalendar &&
        !this.$refs.calendarPopup?.contains(e.target) &&
        !this.$refs.tabsContainer?.contains(e.target)
      ) {
        this.showCustomCalendar = false;
      }
    },
    formatDate(date) {
      return date ? moment(date).format("DD.MM.YYYY") : "-";
    },

    // === OSTATNÉ ===
    checkTimeAndRefresh() {
      const minutes = new Date().getMinutes();
      if ([0, 15, 30, 45].includes(minutes)) {
        this.refreshCharts();
      }
    },

    // === SPOTREBA PREDMETOV ===
    onSubjectConsumptionLoaded(totalConsumption) {
      this.subjectConsumptionTotal = totalConsumption;
    },

    // === CHAT ===
    async sendMessage() {
      const userMsg = this.newMessage.trim();
      if (!userMsg) return;

      this.messages.push({ sender: "user", text: userMsg, session_id: this.sessionId });
      this.newMessage = "";
      this.loading = true;

      await this.$nextTick();
      const chatEl = this.$refs.chatContainer;
      if (chatEl) chatEl.scrollTop = chatEl.scrollHeight;

      try {
        const apiUrl = classroomByPath[this.$route.path].agentUrl + "/rest/post";
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: userMsg, session_id: this.sessionId }),
        });
        const data = await response.json();
        this.messages.push({
          sender: "bot",
          text: data.text || "[No response text]",
          session_id: this.sessionId,
        });
      } catch {
        this.messages.push({
          sender: "bot",
          text: "[Error: Energy Agent is not responding...]",
          session_id: this.sessionId,
        });
      } finally {
        this.loading = false;
        await this.$nextTick();
        const chatEl2 = this.$refs.chatContainer;
        if (chatEl2) chatEl2.scrollTop = chatEl2.scrollHeight;
        this.$nextTick(() => this.$refs.messageInput?.focus());
      }
    },

    onMounted() {
      this.sessionId = "session_" + Math.random().toString(36).substr(2, 9);
      this.messages = [];
      this.messages.push({
        sender: "bot",
        text: "Zdravím, ja som energetický agent z učebne " + (this.classroom?.name || "") + ". Ako Vám môžem pomôcť?",
        session_id: this.sessionId,
      });
    },

    setLoaderTimeout(loaderId) {
      // Zrušiť existujúci timeout, ak existuje
      if (this.loadTimeouts[loaderId]) {
        clearTimeout(this.loadTimeouts[loaderId]);
      }
      // Nastaviť nový timeout
      this.loadTimeouts[loaderId] = setTimeout(() => {
        // Force zmiznutie loaderu po timeout
        switch (loaderId) {
          case 'graphs':
            this.loadingGraphData = false;
            break;
          case 'tips':
            this.loadingTips = false;
            break;
        }
      }, this.loaderTimeoutDuration);
    },

    clearLoaderTimeout(loaderId) {
      if (this.loadTimeouts[loaderId]) {
        clearTimeout(this.loadTimeouts[loaderId]);
        delete this.loadTimeouts[loaderId];
      }
    },
  },

  data() {
    return {
      classroom: null,

      subjectDataList: [],
      subjectDataLoading: false,

      // Stav zariadenia
      statusText: "Checking...",

      // Live meranie (grid)
      power: 0,
      energy: 0,

      currentSubject: "Načítava sa...",

      // Spotreba (vypočítaná z grafov)
      totalConsumed: 0,

      // Cena elektriny
      energyPricePerKwh: 0.20,

      // EnergyInsights premenné
      currentPeriodDays: 1,
      currentPeriodLabel: "Posledných 24 hodín",

      // Spotreba predmetov (z SubjectConsumptionTable)
      subjectConsumptionTotal: 0,

      // Smart Tips
      smartTipsText: "Odporúčania sa aktuálne načítavajú...",
      loadingTips: true,
      loadTimeouts: {},
      loaderTimeoutDuration: 30000, // 30 sekúnd

      // Graf
      loadingGraphData: true,
      graphTimeRange: "Deň",
      customRangeStart: moment().startOf("day").toDate(),
      customRangeEnd: moment().endOf("day").toDate(),
      showCustomCalendar: false,
      currentMonth: new Date(),
      selectedStart: null,
      selectedEnd: null,
      calendarPosition: { top: "0px", left: "0px" },
      dayNames: ["Po", "Ut", "St", "Št", "Pi", "So", "Ne"],

      chartData: {
        labels: [],
        datasets: [],
        subjectMap: {},
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { labels: { color: "#f3f4f6", font: { size: 12 } } } },
        scales: {
          x: { ticks: { color: "#d1d5db", maxRotation: 45, autoSkip: true } },
          y: {
            beginAtZero: true,
            ticks: { color: "#d1d5db" },
            title: { display: true, text: "kWh", color: "#d1d5db" },
          },
        },
      },

      // Chat
      newMessage: "",
      messages: [],
      loading: false,
      sessionId: "session_" + Math.random().toString(36).substr(2, 9),
    };
  },
};